import { NoticeOutlineIcon } from '@carboncopyco/icons';
import { MessageBox } from '@carboncopyco/ui';
import Highcharts from 'highcharts';
import more from 'highcharts/highcharts-more.js';
import draggable from 'highcharts/modules/draggable-points.js';
import solidGauge from 'highcharts/modules/solid-gauge.js';
import { isEqual } from 'lodash-es';
import React, { useEffect, useState } from 'react';

more(Highcharts);
draggable(Highcharts);
solidGauge(Highcharts);

const Chart = Highcharts.chart;

export interface PreviewProps {
  options: Highcharts.Options | null;
  onError: (err: string | null) => void;
}

export const Preview = ({ options = {}, onError }: PreviewProps) => {
  const [hasError, setHasError] = useState<string | null>(null);
  const chartElRef = React.useRef<HTMLDivElement>(null);
  const chartContainerRef = React.useRef<HTMLDivElement>(null);
  const chartInstanceRef = React.useRef<Highcharts.Chart>(null);
  const prevOptions = React.useRef<Highcharts.Options>(options);

  function errorHandler(err: string | null): void {
    setHasError(err);
    onError(err);
  }

  useEffect(() => {
    if (chartContainerRef?.current) {
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          const width = entry.borderBoxSize?.[0].inlineSize;
          if (typeof width === 'number') {
            try {
              chartInstanceRef?.current?.reflow();
            } catch (error) {
              console.debug(error);
            }
          }
        }
      });

      observer.observe(chartContainerRef.current);

      return () => {
        observer.disconnect();
      };
    }

    return;
  }, []);

  function createChart() {
    if (chartElRef?.current) {
      try {
        chartInstanceRef.current = Chart(chartElRef.current, options || {});
        if (typeof hasError === 'string') {
          errorHandler(null);
        }
      } catch (error) {
        const { message } = error as Error;
        errorHandler(message);
      }
    }
  }

  useEffect(() => {
    createChart();
  }, [chartElRef]);

  useEffect(() => {
    if (prevOptions?.current && !isEqual(prevOptions?.current, options)) {
      if (chartInstanceRef?.current) {
        createChart();
        prevOptions.current = options;
      }
    }
  }, [options]);

  return (
    <div
      className="relative h-full w-full"
      ref={chartContainerRef}
    >
      {hasError && (
        <div className="py-2">
          <MessageBox
            label={hasError as string}
            danger
            leftIcon={<NoticeOutlineIcon />}
          />
        </div>
      )}
      <div
        ref={chartElRef}
        style={{ width: '100%', height: '100%' }}
      />
    </div>
  );
};
